import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/services/api";
import toast from "react-hot-toast";
import { Plus, Package, Trash2, Pencil, X, Search, TrendingUp, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const emptyItem = { item_name: "", description: "", sku: "", hsn_sac: "", unit: "pcs", price: "", low_stock_threshold: "" };

export default function InventoryPage() {
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState("");
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [openStockForm, setOpenStockForm] = useState(false);
  const [form, setForm] = useState(emptyItem);
  const [stockForm, setStockForm] = useState({ quantity: "" });
  const [editId, setEditId] = useState(null);
  const [stockItemId, setStockItemId] = useState(null);

  const { data: orgs = [] } = useQuery({ queryKey: ["orgs"], queryFn: async () => (await api.get("/organizations")).data });
  const { data: inventory = [], isLoading } = useQuery({
    queryKey: ["inventory", orgId, q],
    queryFn: async () => (await api.get("/inventory", { params: { organization_id: orgId || undefined, q: q || undefined } })).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, organization_id: orgId || orgs[0]?.id };
      if (!body.organization_id) throw new Error("Create an organization first.");
      if (editId) return (await api.put(`/inventory/${editId}`, body)).data;
      return (await api.post("/inventory", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      toast.success(editId ? "Item updated" : "Item created");
      setOpenForm(false);
      setForm(emptyItem);
      setEditId(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const stockIn = useMutation({
    mutationFn: async () => {
      const qty = parseFloat(stockForm.quantity);
      if (!qty || qty <= 0) throw new Error("Quantity must be positive");
      return (await api.post(`/inventory/${stockItemId}/stock-in`, { quantity: qty })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      toast.success("Stock added");
      setOpenStockForm(false);
      setStockForm({ quantity: "" });
      setStockItemId(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const stockOut = useMutation({
    mutationFn: async () => {
      const qty = parseFloat(stockForm.quantity);
      if (!qty || qty <= 0) throw new Error("Quantity must be positive");
      return (await api.post(`/inventory/${stockItemId}/stock-out`, { quantity: qty })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      toast.success("Stock removed");
      setOpenStockForm(false);
      setStockForm({ quantity: "" });
      setStockItemId(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/inventory/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      toast.success("Item deleted");
    },
  });

  const startEdit = (item) => {
    setEditId(item.id);
    setForm({ ...emptyItem, ...item });
    setOrgId(item.organization_id);
    setOpenForm(true);
  };

  const openStockDialog = (item, type) => {
    setStockItemId(item.id);
    setStockForm({ quantity: "" });
    setOpenStockForm(true);
  };

  const getLowStockItems = () => inventory.filter(item => parseFloat(item.quantity) <= parseFloat(item.low_stock_threshold) && parseFloat(item.quantity) >= 0);
  const getNegativeStockItems = () => inventory.filter(item => parseFloat(item.quantity) < 0);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Stock Management</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter">Inventory.</h1>
        </div>
        <button
          data-testid="new-item-button"
          onClick={() => {
            setEditId(null);
            setForm(emptyItem);
            setOpenForm(true);
          }}
          className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2.5 sm:px-5 sm:py-3 rounded-sm hover:bg-[#002FA7] hover:text-white transition-colors font-semibold self-start md:self-auto text-sm sm:text-base"
        >
          <Plus className="w-4 h-4" /> New Item
        </button>
      </div>

      {/* Negative stock alert */}
      {getNegativeStockItems().length > 0 && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800 dark:text-red-300">Negative stock alert</div>
            <div className="text-sm text-red-700 dark:text-red-400">
              {getNegativeStockItems().length} item(s) have negative inventory
            </div>
          </div>
        </div>
      )}

      {/* Low stock alert */}
      {getLowStockItems().length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800 dark:text-amber-300">Low stock alert</div>
            <div className="text-sm text-amber-700 dark:text-amber-400">
              {getLowStockItems().length} item(s) are below their threshold
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-3 py-2 rounded-sm">
          <Search className="w-4 h-4 text-slate-500" />
          <input
            data-testid="inventory-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, SKU…"
            className="w-full bg-transparent outline-none text-sm"
          />
        </div>
        <select
          data-testid="inventory-org-filter"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          className="flex-1 md:flex-none border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm"
        >
          <option value="">All organizations</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.organization_name}
            </option>
          ))}
        </select>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr className="text-left uppercase text-[10px] tracking-[0.2em] text-slate-500">
              <th className="p-4">Item Name</th>
              <th className="p-4 hidden md:table-cell">SKU</th>
              <th className="p-4 hidden md:table-cell">Stock</th>
              <th className="p-4 hidden lg:table-cell">Unit</th>
              <th className="p-4 hidden lg:table-cell">Price</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!isLoading && inventory.length === 0 && (
              <tr><td colSpan={6} className="p-12 text-center text-slate-500" data-testid="inventory-empty">
                <Package className="w-6 h-6 mx-auto mb-3 text-slate-400" />
                No inventory items yet.
              </td></tr>
            )}
            {inventory.map((item) => {
              const quantity = parseFloat(item.quantity);
              const threshold = parseFloat(item.low_stock_threshold);
              const isNegativeStock = quantity < 0;
              const isLowStock = quantity <= threshold && quantity >= 0;
              const isGoodStock = quantity > threshold;
              
              let rowClass = '';
              let quantityClass = '';
              
              if (isNegativeStock) {
                rowClass = 'bg-red-50/50 dark:bg-red-900/10';
                quantityClass = 'text-red-600 dark:text-red-500';
              } else if (isLowStock) {
                rowClass = 'bg-amber-50/50 dark:bg-amber-900/10';
                quantityClass = 'text-amber-600 dark:text-amber-500';
              } else {
                quantityClass = 'text-green-600 dark:text-green-500';
              }
              
              return (
                <tr key={item.id} className={`border-t border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900 ${rowClass}`} data-testid={`inventory-row-${item.id}`}>
                  <td className="p-4">
                    <Link to={`/app/inventory/${item.id}/history`} className="font-medium hover:text-[#002FA7] hover:underline">
                      {item.item_name}
                    </Link>
                    {item.description && <div className="text-xs text-slate-500 mt-1">{item.description}</div>}
                  </td>
                  <td className="p-4 hidden md:table-cell text-slate-500">{item.sku || "—"}</td>
                  <td className="p-4 hidden md:table-cell">
                    <span className={`font-medium ${quantityClass}`}>
                      {item.quantity}
                    </span>
                  </td>
                  <td className="p-4 hidden lg:table-cell text-slate-500">{item.unit}</td>
                  <td className="p-4 hidden lg:table-cell text-slate-500">₹{item.price}</td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => openStockDialog(item, 'in')}
                        className="p-2 hover:text-green-600" 
                        title="Stock In"
                        data-testid={`stock-in-${item.id}`}
                      >
                        <TrendingUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => startEdit(item)} className="p-2 hover:text-[#002FA7]" data-testid={`edit-item-${item.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.confirm("Delete this item?") && del.mutate(item.id)}
                        className="p-2 hover:text-red-600"
                        data-testid={`delete-item-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Item Form Modal */}
      <AnimatePresence>
        {openForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOpenForm(false)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-auto"
            >
              <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
                <h2 className="text-2xl font-extrabold tracking-tighter">{editId ? "Edit item" : "New item"}</h2>
                <button onClick={() => setOpenForm(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="md:col-span-2">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Organization</span>
                  <select
                    data-testid="item-form-org"
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
                  >
                    <option value="">Choose organization…</option>
                    {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
                  </select>
                </label>
                <label className="md:col-span-2">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Item Name *</span>
                  <input
                    data-testid="item-field-item_name"
                    value={form.item_name}
                    onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                  />
                </label>
                <label className="md:col-span-2">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Description</span>
                  <textarea
                    data-testid="item-field-description"
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                  />
                </label>
                {[
                  ["sku", "SKU"],
                  ["hsn_sac", "HSN/SAC"],
                  ["unit", "Unit"],
                  ["price", "Price"],
                  ["low_stock_threshold", "Low Stock Threshold"]
                ].map(([k, label]) => (
                  <label key={k}>
                    <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">{label}</span>
                    <input
                      data-testid={`item-field-${k}`}
                      type={k === "price" || k === "low_stock_threshold" ? "number" : "text"}
                      step={k === "price" ? "0.01" : "any"}
                      value={form[k] || ""}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                    />
                  </label>
                ))}
              </div>
              <div className="p-6 flex items-center justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
                <button onClick={() => setOpenForm(false)} className="px-5 py-2.5 border border-slate-300 dark:border-slate-700 rounded-sm">Cancel</button>
                <button
                  data-testid="save-item-button"
                  disabled={!form.item_name || save.isPending}
                  onClick={() => save.mutate()}
                  className="px-5 py-2.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] disabled:opacity-50"
                >
                  {save.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stock Form Modal */}
      <AnimatePresence>
        {openStockForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOpenStockForm(false)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-md"
            >
              <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
                <h2 className="text-2xl font-extrabold tracking-tighter">Manage Stock</h2>
                <button onClick={() => setOpenStockForm(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6">
                <label className="block mb-4">
                  <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Quantity</span>
                  <input
                    data-testid="stock-quantity"
                    type="number"
                    step="any"
                    value={stockForm.quantity}
                    onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm outline-none focus:border-slate-900 dark:focus:border-white"
                    placeholder="Enter quantity"
                  />
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => stockIn.mutate()}
                    disabled={!stockForm.quantity || stockIn.isPending}
                    className="flex-1 px-5 py-2.5 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    {stockIn.isPending ? "Adding…" : "Stock In"}
                  </button>
                  <button
                    onClick={() => stockOut.mutate()}
                    disabled={!stockForm.quantity || stockOut.isPending}
                    className="flex-1 px-5 py-2.5 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    {stockOut.isPending ? "Removing…" : "Stock Out"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
